import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import RefreshIcon from '@material-ui/icons/Refresh'
import SendIcon from '@material-ui/icons/Send'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  fetchScopedCentres,
  fetchOnboardingCard,
  fetchOnboardingCardFix,
  fetchOnboardingCentreLinks,
  fetchParentOnboardingBoard,
  fetchWelcomePreview,
  resetOnboardingCardFix,
  saveOnboardingCardFix,
  sendOnboardingCard,
  sendOnboardingForm,
  syncOnboardingEnrolments,
  syncOnboardingSubmissions,
  tickAppDownloads,
  toggleOnboardingStep
} from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

// Persist the picked centre across visits (mirrors ProcurementCenterTeam).
const CENTRE_KEY = 'parentOnboarding.centre'

// The onboarding-form responses Google Sheet (one tab per centre). The team
// opens it to eyeball the raw submissions the board is built from. Mirrors the
// backend PARENT_ONBOARDING_SHEET_ID — keep the two in step if the sheet moves.
// Public (non-secret): access is controlled by sharing the sheet, not the URL.
const ONBOARDING_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1yCnNyOnOx1YW4p-RszaRcO9YFtyOzJWaJq7hEj9nOpI/edit'

// Academic-year filter. Rows synced from enrolments carry the year of their
// admission; rows that came in through the Google Form have none, so they sit
// under NO_YEAR instead of disappearing when a year is picked.
const ALL_YEARS = '__all__'
const NO_YEAR = 'No year'

// The board's default centre: every centre at once. The API takes this literal
// string ("centre=all"), so it doubles as the Select's value.
const ALL_CENTRES = 'all'

// Column headers. Nine columns share the row, so each must fit TWO lines at
// ~100px — anything longer wraps into a four-line stack and the header eats the
// table. Keep these terse; STEP_FULL carries the full wording into the tooltip.
const STEP_HEADERS = {
  orientation: 'Orientation',
  welcome_kit: 'Welcome kit',
  family_1on1: 'Teacher 1:1',
  form_sent: 'Form sent',
  form_submitted: 'Form filled',
  whatsapp_group: 'WA group',
  app_downloaded: 'App downloaded',
  id_card_shared: 'ID card sent',
  director_checkin: 'Director check-in'
}

// The full step names, for the header tooltip — the short label above must not
// cost anyone the meaning.
const STEP_FULL = {
  orientation: 'Orientation done',
  welcome_kit: 'Welcome Kit shared',
  family_1on1: 'Teacher-Family 1:1 done',
  form_sent: 'Onboarding form sent',
  form_submitted: 'Onboarding form submitted',
  whatsapp_group: 'Added to Parent WhatsApp group',
  app_downloaded: 'Openhouse app downloaded',
  id_card_shared: 'Child ID card shared',
  director_checkin: 'Director first-week check-in'
}

const useStyles = makeStyles((theme) => ({
  // openhouse brand: everything on this page renders lowercase (display-only —
  // the underlying data keeps its case). Buttons need their own override since
  // MUI sets text-transform: uppercase on them directly.
  root: {
    padding: theme.spacing(3),
    textTransform: 'lowercase',
    '& .MuiButton-root': { textTransform: 'lowercase' }
  },
  lcPaper: {
    textTransform: 'lowercase',
    '& .MuiButton-root': { textTransform: 'lowercase' }
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  controls: { display: 'flex', alignItems: 'center', gap: theme.spacing(2) },
  centreControl: { minWidth: 220 },
  tableWrap: {
    overflowX: 'auto',
    // The board scrolls inside its own box so the header can stay pinned
    // (Table stickyHeader). maxHeight is measured at runtime to reach exactly
    // the viewport bottom (see tableRef) -- a fixed calc() left dead space.
    overflowY: 'auto',
    '& .MuiTableCell-stickyHeader': { backgroundColor: '#fff' }
  },
  stepHeader: {
    fontSize: 11,
    lineHeight: 1.25,
    // minWidth is what keeps the header to two lines: without it the column
    // shrinks to its content and every label breaks one word per line.
    minWidth: 96,
    maxWidth: 120,
    whiteSpace: 'normal',
    cursor: 'pointer',
    userSelect: 'none'
  },
  stepHeaderActive: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  // The headline number: how many children are fully done, out of everyone on
  // the board.
  onboardedChip: {
    fontWeight: 600,
    backgroundColor: theme.palette.success?.main || '#2e7d32',
    color: '#fff'
  },
  stepCount: {
    display: 'block',
    fontWeight: 600,
    marginTop: 2
  },
  // Rows created straight from a form submission (no Alfred student linked)
  // get an amber wash so the team can tell them apart at a glance.
  formOnlyRow: {
    backgroundColor: 'rgba(255, 179, 0, 0.09)'
  },
  // A frozen cell has to paint an OPAQUE background or the columns scroll
  // through it — which means every row colour has to be repeated on it, and
  // repeated flattened, since a translucent one would let the scrolling
  // content show. The board's stripes come from a global `tr:nth-child(2n)`
  // rule (rgba(158,158,158,.2)); leaving the frozen column plain white made it
  // read as a separate table stuck to the side of the board.
  //   white row      -> #fff
  //   striped row    -> that grey over white
  //   form-only row  -> the amber tint over each of the two above
  //   hover          -> MUI's own opaque #eee, last so it wins on every row
  bodyRow: {
    '& $nameCol': { backgroundColor: '#fff' },
    '&:nth-of-type(even) $nameCol': { backgroundColor: 'rgb(236, 236, 236)' },
    '&$formOnlyRow $nameCol': { backgroundColor: 'rgb(255, 248, 232)' },
    '&$formOnlyRow:nth-of-type(even) $nameCol': {
      backgroundColor: 'rgb(238, 231, 215)'
    },
    '&:hover $nameCol': { backgroundColor: 'rgb(238, 238, 238)' }
  },
  // The board is wider than the screen (9 step columns), so ticking a late step
  // used to scroll the child's name out of view — and the steps on the right
  // are the ones that send things. The name column stays put instead, and the
  // steps scroll under it.
  nameCol: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    backgroundColor: '#fff',
    // A shadow, not a border: it reads as depth (the board passing underneath)
    // rather than as a divider between two tables, and it disappears into the
    // page when there is nothing scrolled under it.
    boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.14)'
  },
  // The header row's own sticky already claims z-index 2, so the corner cell
  // (sticky in BOTH directions) has to outrank it and the frozen body cells.
  nameColHeader: { zIndex: 3 },
  submissionsPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.default
  },
  submissionsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  studentCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  autoTag: { marginLeft: theme.spacing(0.5) },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  }
}))

const studentName = (s) =>
  s?.name ||
  s?.full_name ||
  (s?.user && s.user.full_name) ||
  [s?.first_name, s?.last_name].filter(Boolean).join(' ') ||
  'Unnamed'

const studentImage = (s) => s?.image || (s?.user && s.user.image) || ''

// Human-readable caveats about what the card will show, from the row's
// card_meta (adult count + who lacks a photo). Empty array = nothing to flag.
const cardNotices = (row) => {
  const meta = row?.card_meta
  if (!meta) return []
  const notices = []
  const adults = meta.adults || []
  if (adults.length === 1) {
    notices.push(
      'Only 1 pickup adult was provided, so the card shows a single guardian.'
    )
  } else if (adults.length === 0) {
    notices.push('No pickup adults on record — the card has no guardians.')
  }
  const photographed = adults.filter((a) => a.has_photo)
  const missing = adults.filter((a) => !a.has_photo).map((a) => a.name)
  if (missing.length) {
    notices.push(
      photographed.length
        ? // An adult nobody photographed is left OFF the card now (it used to
          // block the whole card). Name them, and say how to get them on it.
          `${missing.join(' and ')} has no photo on the form, so the card shows ${
            photographed.length === 1 ? 'only 1 adult' : 'the other adults'
          }. Ask the parent to upload one and sync again to include them.`
        : `No photo available for ${missing.join(
            ' and '
          )} — a placeholder circle appears instead.`
    )
  }
  if (!meta.child_photo) {
    notices.push(
      "No child photo available — the child's circle shows a placeholder."
    )
  }
  return notices
}

const ParentOnboarding = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  // False once the user is mapped to a centre — "all centres" then means nothing
  // to them, so the option is not offered.
  const [unrestricted, setUnrestricted] = useState(true)
  // Shared by every Centre Operations page and kept across refreshes.
  const [centreUuid, setCentreUuid, pickCentre] = useOpsCentre({
    legacyKey: CENTRE_KEY
  })
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  const [syncing, setSyncing] = useState(false)
  // The centre's parent-facing links, loaded WITH the board. Held in state on
  // purpose: the Copy button must write to the clipboard on the user's own
  // click. Fetching inside the click handler puts an await before the write,
  // and the browser then refuses it (the bug that broke "Copy prompt").
  const [centreLinks, setCentreLinks] = useState(null)
  const [sopOpen, setSopOpen] = useState(false)

  const [cardOpen, setCardOpen] = useState(false)
  const [cardRow, setCardRow] = useState(null)
  const [cardUrl, setCardUrl] = useState('')
  const [cardLoading, setCardLoading] = useState(false)
  const [cardError, setCardError] = useState('')
  const [cardBlockReasons, setCardBlockReasons] = useState([])
  const [sendPhone, setSendPhone] = useState('')
  const [sendWa, setSendWa] = useState(true)
  const [sending, setSending] = useState(false)

  // "Fix card details" — the centre team repairs a blocked card themselves:
  // replace a bad photo, correct an adult's name/phone. Fixes are stored as an
  // override in Alfred; the parent's form answers are never modified.
  const [fixOpen, setFixOpen] = useState(false)
  const [fixData, setFixData] = useState(null) // GET card_fix payload
  const [fixLoading, setFixLoading] = useState(false)
  const [fixSaving, setFixSaving] = useState(false)
  const [fixResetting, setFixResetting] = useState(false)
  // Errors land IN the dialog (a toast renders beneath the modal).
  const [fixError, setFixError] = useState('')
  // Edited text fields, keyed adult1_name / adult1_phone / adult2_name /
  // adult2_phone — only keys the user actually typed into are sent.
  const [fixFields, setFixFields] = useState({})
  // Chosen replacement photos: {child|adult1|adult2: File}, with object URLs
  // for the little previews.
  const [fixFiles, setFixFiles] = useState({})
  const [fixPreviews, setFixPreviews] = useState({})

  // The scroll box sizes itself to reach the viewport bottom, so the pinned
  // header works without leaving dead white space below (a fixed maxHeight
  // calc() could not know how much room sat above the table).
  const tableRef = useRef(null)
  const [tableMaxH, setTableMaxH] = useState(null)
  useEffect(() => {
    const el = tableRef.current
    if (!el) return undefined
    const fit = () => {
      const top = el.getBoundingClientRect().top
      setTableMaxH(Math.max(260, Math.floor(window.innerHeight - top - 16)))
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [rows.length, loading])

  // "Send welcome message" dialog (rows whose Form step is still unticked).
  // WhatsApp only — the email channel was removed (Alfred emails unreliable);
  // the preview shows the exact message that will reach the parent.
  const [formRow, setFormRow] = useState(null)
  const [formPhone, setFormPhone] = useState('')
  const [formMsg, setFormMsg] = useState('')
  const [formMsgLoading, setFormMsgLoading] = useState(false)
  const [formSending, setFormSending] = useState(false)
  // Why the last send failed, shown inside the dialog (a toast would render
  // beneath it and be invisible).
  const [formError, setFormError] = useState('')

  // Child search (name/phone).
  const [search, setSearch] = useState('')
  // '' = every year. Defaults to the newest year on the board (see the effect
  // below), so the team lands on this year's children, not last year's.
  const [yearFilter, setYearFilter] = useState('')

  // Step filter: click a step's column header to cycle
  // all -> only ticked -> only unticked -> all.
  const [stepFilter, setStepFilter] = useState(null) // {key, mode:'done'|'pending'}
  const cycleStepFilter = (key) =>
    setStepFilter((prev) => {
      if (!prev || prev.key !== key) return { key, mode: 'done' }
      if (prev.mode === 'done') return { key, mode: 'pending' }
      return null
    })

  // Load centres once; keep the persisted choice if it still exists, else fall
  // back to every centre. Defaulting to "all" (rather than whichever centre
  // happened to sort first) means the board opens on the whole picture and the
  // team narrows down deliberately.
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const data = await fetchScopedCentres()
        const list = data?.results || data || []
        if (!active) return
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setCentreUuid((prev) => {
          const allOk = Boolean(data?.unrestricted)
          if (
            prev &&
            ((prev === ALL_CENTRES && allOk) ||
              list.some((c) => c.uuid === prev))
          ) {
            return prev
          }
          return allOk ? ALL_CENTRES : list[0]?.uuid || ''
        })
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    setSearch('')
  }, [centreUuid])

  const loadBoard = useCallback(async () => {
    if (!centreUuid) return
    setLoading(true)
    try {
      const data = await fetchParentOnboardingBoard(centreUuid)
      const list = Array.isArray(data) ? data : data?.results || []
      // Alphabetical by child name — the API returns newest-added first,
      // which reads as random on the board (user question 2026-07-11).
      setRows(
        list.sort((a, b) =>
          studentName(a.student).localeCompare(studentName(b.student))
        )
      )
    } catch (e) {
      showToast('Could not load onboarding tracker', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [centreUuid])

  useEffect(() => {
    loadBoard()
  }, [loadBoard])

  // Refresh = sync, then reload. A beat job already pulls both sources every
  // 30 minutes, so the board is normally current on its own; this is the "I
  // don't want to wait for the next run" path. Both legs are best-effort — a
  // failing source must not stop the other, and the board reloads regardless
  // so Refresh always at least refreshes.
  const handleRefresh = async () => {
    setSyncing(true)
    try {
      // App-downloaded is a third, independent sweep. It's best-effort — a
      // failure here must not turn a good enrolment/form refresh into an error,
      // so it stays out of the error branches below.
      const [enrolments, forms, appDl] = await Promise.allSettled([
        syncOnboardingEnrolments(),
        syncOnboardingSubmissions(),
        tickAppDownloads()
      ])

      const added =
        (enrolments.status === 'fulfilled' ? enrolments.value.created || 0 : 0) +
        (forms.status === 'fulfilled' ? forms.value.tracked || 0 : 0)
      const appTicked =
        appDl.status === 'fulfilled' ? appDl.value.ticked || 0 : 0

      if (enrolments.status === 'rejected' && forms.status === 'rejected') {
        showToast('Could not reach the enrolments or the form', constants.ERROR)
      } else if (enrolments.status === 'rejected') {
        showToast('Enrolments could not be synced', constants.ERROR)
      } else if (forms.status === 'rejected') {
        showToast('Form responses could not be synced', constants.ERROR)
      } else {
        const bits = []
        if (added) bits.push(`${added} new ${added === 1 ? 'child' : 'children'}`)
        if (appTicked)
          bits.push(`${appTicked} app download${appTicked === 1 ? '' : 's'}`)
        showToast(
          bits.length ? `Up to date — ${bits.join(', ')} added` : 'Up to date — nothing new',
          constants.SUCCESS
        )
      }
    } finally {
      await loadBoard()
      setSyncing(false)
    }
  }

  const handleToggle = async (row, stepKey, checked) => {
    // Optimistic: reflect the tick immediately, roll back on failure.
    const prevRows = rows
    setRows((rs) =>
      rs.map((r) =>
        r.uuid === row.uuid
          ? {
              ...r,
              steps: r.steps.map((s) =>
                s.key === stepKey ? { ...s, checked } : s
              ),
              done_count: r.done_count + (checked ? 1 : -1),
              completed: r.done_count + (checked ? 1 : -1) === r.total
            }
          : r
      )
    )
    try {
      const updated = await toggleOnboardingStep(row.uuid, stepKey, checked)
      setRows((rs) => rs.map((r) => (r.uuid === updated.uuid ? updated : r)))
    } catch (e) {
      setRows(prevRows)
      showToast('Could not update step', constants.ERROR)
    }
  }

  // Per-step tick counts for the column headers ("4/41").
  const stepDone = (r, key) => !!r.steps?.find((s) => s.key === key)?.checked
  const formAlreadyIn = (r) => stepDone(r, 'form_submitted')

  // The rows the board is actually showing: this centre's children in the
  // SELECTED academic year (or all years). The headline count and the per-step
  // column totals are scoped to THIS, not to every year at once — otherwise the
  // denominator counts last year's children and the form-only "no year" bucket
  // too, so "0/81" sits over a list of 35. Search and the step-column filter
  // narrow only the visible table below, never these totals.
  const inSelectedYear = (r) =>
    !yearFilter ||
    yearFilter === ALL_YEARS ||
    (r.academic_year || NO_YEAR) === yearFilter
  const yearRows = rows.filter(inSelectedYear)

  const stepCounts = yearRows.reduce((acc, r) => {
    r.steps?.forEach((s) => {
      acc[s.key] = (acc[s.key] || 0) + (s.checked ? 1 : 0)
    })
    return acc
  }, {})

  // A child is "onboarded" only when EVERY step is ticked — a part-done
  // checklist is not an onboarded child. Counted off the row's own steps, so a
  // step added later raises the bar automatically instead of silently passing.
  const isOnboarded = (r) =>
    (r.steps?.length || 0) > 0 && r.steps.every((s) => s.checked)
  const onboardedCount = yearRows.filter(isOnboarded).length

  // Academic years present on this centre's board, newest first. Rows from the
  // form bridge carry no year — they group under NO_YEAR rather than vanish.
  // Real years newest-first; the "no year" bucket (form-only rows, no admission)
  // always sits last so it can never become the default the board opens on.
  const yearOptions = Array.from(
    new Set(rows.map((r) => r.academic_year || NO_YEAR))
  ).sort((a, b) => {
    if (a === NO_YEAR) return 1
    if (b === NO_YEAR) return -1
    return b.localeCompare(a)
  })

  // Land on the newest year rather than on every year at once — the board is
  // for onboarding this year's children. Re-runs when the centre changes and
  // the picked year isn't on the new board.
  useEffect(() => {
    if (!yearOptions.length) return
    if (yearFilter === ALL_YEARS || yearOptions.includes(yearFilter)) return
    setYearFilter(yearOptions[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  // Client-side child search (name or phone, case/space-insensitive) + the
  // step filter from the column headers. The academic year is already applied
  // in yearRows, so the totals above stay put while these narrow the table.
  const visibleRows = yearRows.filter((r) => {
    if (stepFilter) {
      const done = stepDone(r, stepFilter.key)
      if (stepFilter.mode === 'done' ? !done : done) return false
    }
    const q = search.trim().toLowerCase()
    if (!q) return true
    const name = studentName(r.student).toLowerCase()
    const phone = String(r.parent_phone || '').replace(/\s+/g, '')
    return name.includes(q) || phone.includes(q.replace(/\s+/g, ''))
  })

  const openSendForm = async (row) => {
    setFormRow(row)
    setFormPhone(row.parent_phone || '')
    setFormMsg('')
    setFormError('')
    setFormMsgLoading(true)
    try {
      const data = await fetchWelcomePreview(row.uuid)
      setFormMsg(data?.message || '')
      if (data?.phone && !row.parent_phone) setFormPhone(data.phone)
    } catch (e) {
      showToast('Could not load the message preview', constants.ERROR)
    } finally {
      setFormMsgLoading(false)
    }
  }

  const handleSendForm = async () => {
    if (!formPhone) {
      setFormError('A WhatsApp number is needed')
      return
    }
    setFormSending(true)
    setFormError('')
    try {
      const data = await sendOnboardingForm(formRow.uuid, { phone: formPhone })
      const wa = data?.results?.whatsapp
      if (wa?.ok) {
        showToast('Welcome message sent on WhatsApp', constants.SUCCESS)
        if (data?.onboarding) {
          setRows((rs) =>
            rs.map((x) => (x.uuid === data.onboarding.uuid ? data.onboarding : x))
          )
        }
        setFormRow(null)
      } else {
        // The reason has to land IN the dialog: a toast renders under the modal
        // (SweetAlert 1060 vs MUI Dialog 1300), so a failed send looked like a
        // button that did nothing at all.
        setFormError(wa?.error || 'WhatsApp send failed')
      }
    } catch (e) {
      setFormError(
        e?.response?.data?.detail || 'Could not send the welcome message'
      )
    } finally {
      setFormSending(false)
    }
  }

  const loadCard = async (row) => {
    setCardError('')
    setCardBlockReasons([])
    setCardUrl('')
    setCardLoading(true)
    try {
      const blob = await fetchOnboardingCard(row.uuid, 'png')
      setCardUrl(URL.createObjectURL(blob))
    } catch (e) {
      if (e?.blockReasons?.length) {
        // Intentionally blocked (bad/missing photos) — reasons, not an error.
        setCardBlockReasons(e.blockReasons)
      } else {
        // Never guess at the cause. This used to read "the parent photos may
        // be large — please retry", which was a guess, and a wrong one: the
        // real reason was usually a blocked card (one pickup adult) that the
        // gate had missed. Say only what we actually know.
        setCardError(
          e?.serverDetail ||
            'Could not generate the card — the server did not say why. Try again, and tell tech if it keeps failing.'
        )
      }
    } finally {
      setCardLoading(false)
    }
  }

  const openCard = (row) => {
    setCardRow(row)
    setCardUrl('')
    setCardError('')
    setCardOpen(true)
    setSendPhone(row.parent_phone || '')
    setSendWa(true)
    loadCard(row)
  }

  const closeCard = () => {
    if (cardUrl) URL.revokeObjectURL(cardUrl)
    setCardUrl('')
    setCardRow(null)
    setCardOpen(false)
  }

  // ---- "Fix card details" ----------------------------------------------

  const clearFixSelections = () => {
    Object.values(fixPreviews).forEach((u) => URL.revokeObjectURL(u))
    setFixFiles({})
    setFixPreviews({})
    setFixFields({})
    setFixError('')
  }

  const openFix = async () => {
    if (!cardRow) return
    clearFixSelections()
    setFixOpen(true)
    setFixData(null)
    setFixLoading(true)
    try {
      setFixData(await fetchOnboardingCardFix(cardRow.uuid))
    } catch (e) {
      setFixError(
        e?.response?.data?.detail || 'Could not load the card details'
      )
    } finally {
      setFixLoading(false)
    }
  }

  const closeFix = () => {
    clearFixSelections()
    setFixOpen(false)
    setFixData(null)
  }

  // What the form/fixes currently hold for a field — the baseline the inputs
  // start from, and what edits are compared against before being sent.
  const fixPrefill = (slot, key) => {
    const a = fixData?.adults?.[slot === 'adult1' ? 0 : 1]
    return (a && a[key]) || ''
  }

  const pickFixPhoto = (slot) => (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // so the same file can be picked again after an error
    if (!file) return
    const name = (file.name || '').toLowerCase()
    // Instant refusals for the two commonest wrong files — the server checks
    // everything again (including group photos, which need a face probe).
    if (name.endsWith('.heic') || name.endsWith('.heif')) {
      setFixError(
        'That is an iPhone HEIC file, which the card cannot read — share it from the phone as a JPG and upload that instead.'
      )
      return
    }
    if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
      setFixError('That is a PDF, not a photo — please upload a JPG or PNG image.')
      return
    }
    setFixError('')
    setFixFiles((f) => ({ ...f, [slot]: file }))
    setFixPreviews((p) => {
      if (p[slot]) URL.revokeObjectURL(p[slot])
      return { ...p, [slot]: URL.createObjectURL(file) }
    })
  }

  // Only actual changes are sent: photos picked, and typed values that differ
  // from what is already on record.
  const fixChangedFields = () => {
    const fields = {}
    Object.entries(fixFields).forEach(([key, value]) => {
      const slot = key.startsWith('adult1') ? 'adult1' : 'adult2'
      const attr = key.endsWith('_name') ? 'name' : 'phone'
      const trimmed = (value || '').trim()
      if (trimmed && trimmed !== fixPrefill(slot, attr)) fields[key] = trimmed
    })
    return fields
  }

  const fixDirty =
    Object.keys(fixFiles).length > 0 ||
    Object.keys(fixChangedFields()).length > 0

  const handleFixSave = async () => {
    if (!cardRow) return
    setFixSaving(true)
    setFixError('')
    try {
      const res = await saveOnboardingCardFix(cardRow.uuid, {
        fields: fixChangedFields(),
        files: fixFiles
      })
      if (res?.onboarding) {
        setRows((rs) =>
          rs.map((x) => (x.uuid === res.onboarding.uuid ? res.onboarding : x))
        )
        setCardRow(res.onboarding)
      }
      showToast('Card details fixed', constants.SUCCESS)
      closeFix()
      // The card dialog is still open underneath — re-render the card from
      // the fixed details straight away.
      loadCard(res?.onboarding || cardRow)
    } catch (e) {
      setFixError(e?.response?.data?.detail || 'Could not save the fixes')
    } finally {
      setFixSaving(false)
    }
  }

  const handleFixReset = async () => {
    if (!cardRow) return
    setFixResetting(true)
    setFixError('')
    try {
      const res = await resetOnboardingCardFix(cardRow.uuid)
      if (res?.onboarding) {
        setRows((rs) =>
          rs.map((x) => (x.uuid === res.onboarding.uuid ? res.onboarding : x))
        )
        setCardRow(res.onboarding)
      }
      clearFixSelections()
      setFixLoading(true)
      try {
        setFixData(await fetchOnboardingCardFix(cardRow.uuid))
      } finally {
        setFixLoading(false)
      }
      // The card dialog underneath must not keep showing a render built from
      // the fixes that were just undone.
      loadCard(res?.onboarding || cardRow)
      showToast("Back to the parent's form answers", constants.SUCCESS)
    } catch (e) {
      setFixError(e?.response?.data?.detail || 'Could not undo the fixes')
    } finally {
      setFixResetting(false)
    }
  }

  // One photo line in the fix dialog: preview (or initial), the photo's state
  // in plain words, and the replace/upload picker.
  const fixPhotoRow = (slot, photo, personName) => {
    const picked = fixPreviews[slot]
    let line
    let isProblem = false
    if (picked) {
      line = 'New photo chosen — it saves when you press save.'
    } else if (photo?.reason) {
      line = photo.reason
      isProblem = true
    } else if (photo?.status === 'ok') {
      line = 'Photo looks fine.'
    } else if (photo?.status === 'fetch_failed') {
      line = 'The photo could not be checked just now — this usually clears on its own.'
    } else if (photo?.present) {
      line = 'Photo not checked yet.'
    } else {
      line = 'No photo uploaded.'
      isProblem = true
    }
    return (
      <Box display="flex" alignItems="center" mt={1.5} style={{ gap: 12 }}>
        <Avatar
          variant="rounded"
          src={picked || undefined}
          style={{ width: 56, height: 56 }}
        >
          {(personName || '?').trim().charAt(0).toUpperCase()}
        </Avatar>
        <Box flexGrow={1}>
          <Typography
            variant="body2"
            color={isProblem ? 'error' : 'textSecondary'}
          >
            {line}
          </Typography>
        </Box>
        <Button variant="outlined" color="primary" size="small" component="label">
          {photo?.present || picked ? 'Replace photo' : 'Upload photo'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={pickFixPhoto(slot)}
          />
        </Button>
      </Box>
    )
  }

  // The fix dialog's section boxes: red wash while the slot has a problem the
  // user hasn't picked a replacement for yet.
  const fixSectionStyle = (bad) => ({
    border: `1px solid ${bad ? '#d32f2f' : 'rgba(0, 0, 0, 0.12)'}`,
    background: bad ? '#fdf3f2' : 'transparent',
    borderRadius: 8,
    padding: '12px 14px',
    marginTop: 12
  })

  const downloadCard = async (format) => {
    if (!cardRow) return
    try {
      const blob = await fetchOnboardingCard(cardRow.uuid, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${studentName(cardRow.student).replace(
        /\s+/g,
        '_'
      )}_id_card.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      showToast('Could not download card', constants.ERROR)
    }
  }

  const handleSend = async () => {
    if (!cardRow) return
    const channels = [
      // WhatsApp only — the email channel is not offered from this tab.
      'whatsapp'
    ]
    if (!channels.length) {
      setCardError('Pick at least one channel')
      return
    }
    setSending(true)
    setCardError('')
    try {
      const res = await sendOnboardingCard(cardRow.uuid, {
        channels,
        phone: sendPhone,
        // no email channel from this tab
      })
      const r = res?.results || {}
      const ok = Object.keys(r).filter((k) => r[k]?.ok)
      const failed = Object.keys(r).filter((k) => !r[k]?.ok)
      if (ok.length) {
        showToast(`Card sent: ${ok.join(', ')}`, constants.SUCCESS)
      }
      // Failures belong in the dialog — a toast fires behind it and is never seen.
      if (failed.length) {
        setCardError(
          failed.map((k) => `${k}: ${r[k]?.error || 'error'}`).join(' · ')
        )
      }
      if (res?.onboarding) {
        setRows((rs) =>
          rs.map((x) => (x.uuid === res.onboarding.uuid ? res.onboarding : x))
        )
      }
    } catch (e) {
      setCardError(e?.response?.data?.detail || 'Could not send card')
    } finally {
      setSending(false)
    }
  }

  const stepKeys =
    rows[0]?.steps?.map((s) => s.key) || Object.keys(STEP_HEADERS)

  // In the all-centres view a child's centre is the one thing the row cannot
  // otherwise tell you, so the column earns its place — and only there.
  const allCentres = centreUuid === ALL_CENTRES

  useEffect(() => {
    if (!centreUuid || centreUuid === ALL_CENTRES) {
      setCentreLinks(null)
      return
    }
    let live = true
    fetchOnboardingCentreLinks(centreUuid)
      .then((d) => live && setCentreLinks(d))
      .catch(() => live && setCentreLinks(null))
    return () => {
      live = false
    }
  }, [centreUuid])

  // Synchronous — the click is still "active", so the clipboard write is allowed.
  const copyEnrolmentLink = () => {
    const url = centreLinks?.enrolment_form
    if (!url) {
      showToast(
        `No enrolment form link for ${centreLinks?.centre || 'this centre'} yet — add it to the Links tab of the onboarding sheet`,
        constants.ERROR
      )
      return
    }
    const done = (ok) =>
      showToast(
        ok
          ? `Enrolment form link copied — ${centreLinks.centre}`
          : 'Could not copy — open the link and copy it by hand',
        ok ? constants.SUCCESS : constants.ERROR
      )
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => done(true), () => done(false))
      return
    }
    try {
      const ta = document.createElement('textarea')
      ta.value = url
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      done(ok)
    } catch (e) {
      done(false)
    }
  }

  return (
    <Box className={classes.root}>
      <div className={classes.headerRow}>
        <div>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography variant="h5">Parent Onboarding</Typography>
            <Tooltip title="How this page works">
              <IconButton size="small" onClick={() => setSopOpen(true)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {yearRows.length ? (
              <Tooltip
                arrow
                title="A child counts as onboarded only once every step on their row is ticked"
              >
                <Chip
                  size="small"
                  className={classes.onboardedChip}
                  label={`${onboardedCount}/${yearRows.length} onboarded`}
                />
              </Tooltip>
            ) : null}
          </Box>
          <Typography variant="body2" color="textSecondary">
            Track each child&apos;s onboarding checklist. Tick steps as the team
            completes them.
          </Typography>
        </div>
        <div className={classes.controls}>
          {/* Search sits leftmost, under the Child column it searches — the
              eye lands on the names and the box is right there. */}
          <TextField
            size="small"
            variant="outlined"
            placeholder="search name / phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 210 }}
          />
          <FormControl
            variant="outlined"
            size="small"
            className={classes.centreControl}
          >
            <InputLabel>Centre</InputLabel>
            <Select
              label="Centre"
              value={centreUuid}
              onChange={(e) => pickCentre(e.target.value)}
            >
              {unrestricted && (
                <MenuItem value={ALL_CENTRES}>All centres</MenuItem>
              )}
              {centres.map((c) => (
                <MenuItem key={c.uuid} value={c.uuid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Academic year</InputLabel>
            <Select
              label="Academic year"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
              <MenuItem value={ALL_YEARS}>All years</MenuItem>
            </Select>
          </FormControl>
          {/* The centre team is asked for the enrolment form constantly — a parent
              rings up, or turns up at the desk, and cannot find it. Give them
              the link in one click instead of a hunt through the sheet. */}
          {!allCentres && (
            <Tooltip
              title={
                centreLinks?.enrolment_form
                  ? `Copy ${centreLinks.centre}'s enrolment form link, to send to a parent`
                  : 'No enrolment form link for this centre yet — add it to the Links tab of the onboarding sheet'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<FileCopyOutlinedIcon />}
                  disabled={!centreLinks?.enrolment_form}
                  onClick={copyEnrolmentLink}
                >
                  Copy enrolment form
                </Button>
              </span>
            </Tooltip>
          )}
          {/* Straight link to the responses sheet — the team asks "where does
              this data come from?" constantly; one click beats digging through
              Drive. Global (one sheet, per-centre tabs), so it shows for every
              centre selection. */}
          <Tooltip title="Open the onboarding-form responses Google Sheet in a new tab">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<OpenInNewIcon />}
              href={ONBOARDING_SHEET_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Form responses
            </Button>
          </Tooltip>
          <Tooltip title="Refresh — pulls the latest enrolments and form responses, then reloads the board">
            <span>
              <IconButton
                onClick={handleRefresh}
                size="small"
                disabled={syncing}
              >
                {syncing ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <div className={classes.empty}>
          <Typography variant="body1">
            No students on the onboarding tracker yet.
          </Typography>
          <Typography variant="body2">
            Pick a centre and press refresh (⟳, next to the search box) to pull
            in enrolments and form responses.
          </Typography>
        </div>
      ) : (
        <div
          ref={tableRef}
          className={classes.tableWrap}
          style={tableMaxH ? { maxHeight: tableMaxH } : undefined}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.nameCol} ${classes.nameColHeader}`}>
                  Child
                </TableCell>
                {allCentres ? <TableCell>Centre</TableCell> : null}
                <TableCell>Phone</TableCell>
                {stepKeys.map((key) => (
                  <TableCell
                    key={key}
                    align="center"
                    className={`${classes.stepHeader} ${
                      stepFilter?.key === key ? classes.stepHeaderActive : ''
                    }`}
                    onClick={() => cycleStepFilter(key)}
                  >
                    <Tooltip
                      arrow
                      title={`${STEP_FULL[key] || key} — ${
                        stepFilter?.key === key
                          ? stepFilter.mode === 'done'
                            ? 'showing ticked, click for unticked'
                            : 'showing unticked, click to clear'
                          : 'click to show only ticked children'
                      }`}
                    >
                      <span>
                        {STEP_HEADERS[key] || key}
                        <span className={classes.stepCount}>
                          {stepCounts[key] || 0}/{yearRows.length}
                          {stepFilter?.key === key
                            ? stepFilter.mode === 'done'
                              ? ' ✓'
                              : ' ✗'
                            : ''}
                        </span>
                      </span>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell align="center">Progress</TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  key={row.uuid}
                  hover
                  className={`${classes.bodyRow} ${
                    row.student?.from_form ? classes.formOnlyRow : ''
                  }`}
                >
                  <TableCell className={classes.nameCol}>
                    <div className={classes.studentCell}>
                      <Avatar
                        src={studentImage(row.student)}
                        alt={studentName(row.student)}
                      >
                        {studentName(row.student).charAt(0)}
                      </Avatar>
                      <div>
                        <Typography variant="body2">
                          {studentName(row.student)}
                          {row.student?.from_form ? (
                            <Chip
                              size="small"
                              label="form-only"
                              className={classes.autoTag}
                              style={{ height: 18, fontSize: 10 }}
                            />
                          ) : null}
                        </Typography>
                        {row.student?.from_form ? (
                          <Typography variant="caption" color="textSecondary">
                            not enrolled in alfred yet
                          </Typography>
                        ) : null}
                        {row.student?.standard ? (
                          <Typography variant="caption" color="textSecondary">
                            {row.student.standard}
                          </Typography>
                        ) : null}
                      </div>
                    </div>
                  </TableCell>
                  {allCentres ? (
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {row.centre?.name || '—'}
                      </Typography>
                    </TableCell>
                  ) : null}
                  <TableCell>
                    {row.parent_phone ? (
                      <Typography variant="body2" noWrap>
                        {row.parent_phone}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  {row.steps.map((step) => {
                    // The form tick is auto-populated by the sheet sync; once
                    // the parent has submitted it is frozen (can't be unticked).
                    // "App downloaded" is always frozen — it's decided from
                    // Openhouse app data (login / registered device), never by
                    // hand.
                    const frozen =
                      (step.key === 'form_submitted' && !!step.checked) ||
                      step.key === 'app_downloaded'
                    return (
                      <TableCell key={step.key} align="center">
                        <Tooltip
                          title={
                            step.key === 'app_downloaded'
                              ? 'Detected automatically from Openhouse app data (member-app login or a registered device) — cannot be set by hand'
                              : frozen
                              ? 'Auto-ticked when the parent submits the onboarding form'
                              : step.auto
                              ? `${step.label} (auto)`
                              : step.label
                          }
                          arrow
                        >
                          <span>
                            <Checkbox
                              color="primary"
                              size="small"
                              checked={!!step.checked}
                              disabled={frozen}
                              onChange={(e) =>
                                handleToggle(row, step.key, e.target.checked)
                              }
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    )
                  })}
                  <TableCell align="center">
                    <Chip
                      size="small"
                      color={row.completed ? 'primary' : 'default'}
                      label={`${row.done_count}/${row.total}`}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {/* Shown on every row, disabled once the form is in: the
                        welcome note IS the form invitation, so there is nothing
                        left to invite them to. Disabled-with-a-reason beats a
                        missing button — an absent icon reads as a bug. */}
                    <Tooltip
                      title={
                        formAlreadyIn(row)
                          ? 'Form already submitted — no welcome note needed'
                          : 'Send welcome message to parent'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={formAlreadyIn(row)}
                          onClick={() => openSendForm(row)}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="ID card">
                      <IconButton size="small" onClick={() => openCard(row)}>
                        <CreditCardIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={!!formRow}
        onClose={() => setFormRow(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.lcPaper }}
      >
        <DialogTitle>
          {formRow
            ? `Send welcome message — ${studentName(formRow.student)}`
            : 'Send welcome message'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Goes out on WhatsApp only. The &ldquo;Onboarding form sent&rdquo;
            step ticks when it sends; &ldquo;form submitted&rdquo; still ticks
            only when the parent&apos;s submission syncs back.
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            label="WhatsApp number"
            value={formPhone}
            onChange={(e) => setFormPhone(e.target.value)}
          />
          <Typography variant="caption" color="textSecondary">
            This exact message will be sent:
          </Typography>
          {formMsgLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box
              style={{
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                lineHeight: 1.45,
                background: '#f6f5f2',
                border: '1px solid #e0ddd6',
                borderRadius: 8,
                padding: '10px 12px',
                maxHeight: 260,
                overflowY: 'auto',
                textTransform: 'none'
              }}
            >
              {formMsg || 'Message preview unavailable.'}
            </Box>
          )}
          {!!formError && (
            <Box
              mt={1.5}
              style={{
                fontSize: 13,
                lineHeight: 1.45,
                color: '#8a1f11',
                background: '#fdecea',
                border: '1px solid #f5c6cb',
                borderRadius: 8,
                padding: '10px 12px',
                textTransform: 'none'
              }}
            >
              <b>Not sent.</b> {formError}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormRow(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={formSending || formMsgLoading || !formMsg}
            onClick={handleSendForm}
          >
            {formSending ? 'Sending…' : 'Send on WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cardOpen}
        onClose={closeCard}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.lcPaper }}
      >
        <DialogTitle>
          {cardRow ? `${studentName(cardRow.student)} — ID card` : 'ID card'}
        </DialogTitle>
        <DialogContent>
          {cardLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={6}
            >
              <CircularProgress />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                Generating the ID card…
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Fetching the parent photos, this can take a few seconds.
              </Typography>
            </Box>
          ) : cardBlockReasons.length ? (
            <Box py={4} px={2}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                ID card not generated
              </Typography>
              {cardBlockReasons.map((reason) => (
                <Typography key={reason} variant="body2" gutterBottom>
                  • {reason}
                </Typography>
              ))}
              {cardRow?.card_meta?.can_fix ? (
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={openFix}
                  >
                    Fix card details
                  </Button>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginTop: 8 }}
                  >
                    Replace the bad photo or correct a detail yourself — the
                    parent&apos;s form answers stay untouched. Or ask the
                    parent to re-submit the form and sync again.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  Fix the form response (or ask the parent to re-submit) and
                  sync again — the card unblocks automatically.
                </Typography>
              )}
            </Box>
          ) : cardError ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={5}
            >
              <Typography
                variant="body2"
                color="error"
                align="center"
                gutterBottom
              >
                {cardError}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => cardRow && loadCard(cardRow)}
              >
                Retry
              </Button>
            </Box>
          ) : cardUrl ? (
            <>
              {cardRow?.card_meta?.fixed ? (
                // A manual correction changes who may collect the child — it
                // is never invisible: the card says who fixed it and when.
                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  color="textSecondary"
                >
                  ✎ details manually corrected by {cardRow.card_meta.fixed.by}
                  {cardRow.card_meta.fixed.at
                    ? ` on ${new Date(
                        cardRow.card_meta.fixed.at
                      ).toLocaleDateString()}`
                    : ''}{' '}
                  — open “fix details” to review or undo.
                </Typography>
              ) : null}
              {cardNotices(cardRow).map((notice) => (
                <Typography
                  key={notice}
                  variant="caption"
                  display="block"
                  gutterBottom
                  style={{ color: '#b26a00' }}
                >
                  ⚠ {notice}
                </Typography>
              ))}
              <img
                src={cardUrl}
                alt="Parent ID card"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </>
          ) : null}
          {cardUrl ? (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Send to parent
              </Typography>
              {cardRow?.card_meta?.wa_caption ? (
                <>
                  <Typography
                    variant="caption"
                    display="block"
                    gutterBottom
                    color="textSecondary"
                  >
                    This exact WhatsApp message goes with the card:
                  </Typography>
                  <Box
                    mb={1.5}
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: 13,
                      lineHeight: 1.45,
                      background: '#f6f5f2',
                      border: '1px solid #e0ddd6',
                      borderRadius: 8,
                      padding: '10px 12px',
                      maxHeight: 260,
                      overflowY: 'auto',
                      textTransform: 'none'
                    }}
                  >
                    {cardRow.card_meta.wa_caption}
                  </Box>
                </>
              ) : null}
              <TextField
                label="WhatsApp phone"
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                value={sendPhone}
                onChange={(e) => setSendPhone(e.target.value)}
              />
              {/* Email to the parent is OFF from this tab (2026-07-14). The card
                  goes over WhatsApp only; the sender/channel code is untouched,
                  just not offered here. */}
              <Box display="flex" alignItems="center" mt={1}>
                <Box flexGrow={1} />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={sending}
                >
                  {sending ? 'Sending…' : 'Send'}
                </Button>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {cardRow?.card_meta?.can_fix && cardUrl ? (
            // Also reachable when the card renders fine — a wrong phone or a
            // misspelled name is worth fixing even on an unblocked card.
            <Button onClick={openFix} disabled={cardLoading}>
              Fix details
            </Button>
          ) : null}
          <Button
            onClick={() => downloadCard('png')}
            disabled={cardLoading || !cardUrl}
          >
            Download PNG
          </Button>
          <Button onClick={closeCard} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fixOpen}
        onClose={fixSaving || fixResetting ? undefined : closeFix}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.lcPaper }}
      >
        <DialogTitle>
          {cardRow
            ? `Fix card details — ${studentName(cardRow.student)}`
            : 'Fix card details'}
        </DialogTitle>
        <DialogContent>
          {fixLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : fixData ? (
            <>
              <Typography variant="caption" color="textSecondary" display="block">
                Pre-filled from the parent&apos;s form — change only what&apos;s
                wrong, everything else keeps the parent&apos;s answers. The form
                itself is never modified.
              </Typography>
              {fixData.override ? (
                <Typography
                  variant="caption"
                  display="block"
                  style={{ color: '#3a5a8c', marginTop: 4 }}
                >
                  ✎ already carries fixes by {fixData.override.by}
                  {fixData.override.at
                    ? ` (${new Date(fixData.override.at).toLocaleDateString()})`
                    : ''}
                  {' '}— reset below brings back the form answers.
                </Typography>
              ) : null}

              <Box style={fixSectionStyle(false)}>
                <Typography variant="overline" color="textSecondary">
                  Child
                </Typography>
                <Typography variant="body2">{fixData.child?.name}</Typography>
                {fixPhotoRow('child', fixData.child?.photo, fixData.child?.name)}
              </Box>

              {(fixData.adults || []).map((adult, i) => {
                const slot = `adult${i + 1}`
                const bad = Boolean(adult.photo?.reason) && !fixPreviews[slot]
                return (
                  <Box key={slot} style={fixSectionStyle(bad)}>
                    <Typography variant="overline" color="textSecondary">
                      Pickup adult {i + 1}
                    </Typography>
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      style={{ gap: 12 }}
                      mt={0.5}
                    >
                      <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        style={{ flex: 1, minWidth: 160 }}
                        value={
                          fixFields[`${slot}_name`] !== undefined
                            ? fixFields[`${slot}_name`]
                            : adult.name || ''
                        }
                        onChange={(e) => {
                          // React 16 pools synthetic events — e.target is null
                          // by the time a functional setState updater runs, so
                          // the value MUST be captured synchronously here.
                          // (Typing crashed the whole page before this.)
                          const value = e.target.value
                          setFixFields((f) => ({
                            ...f,
                            [`${slot}_name`]: value
                          }))
                        }}
                      />
                      <TextField
                        label="Phone"
                        variant="outlined"
                        size="small"
                        style={{ flex: 1, minWidth: 160 }}
                        value={
                          fixFields[`${slot}_phone`] !== undefined
                            ? fixFields[`${slot}_phone`]
                            : adult.phone || ''
                        }
                        onChange={(e) => {
                          const value = e.target.value
                          setFixFields((f) => ({
                            ...f,
                            [`${slot}_phone`]: value
                          }))
                        }}
                      />
                    </Box>
                    {fixPhotoRow(slot, adult.photo, adult.name)}
                  </Box>
                )
              })}

              {fixData.can_add_adult2 ? (
                <Box
                  style={{
                    ...fixSectionStyle(false),
                    borderStyle: 'dashed'
                  }}
                >
                  <Typography variant="overline" color="textSecondary">
                    Second pickup adult (optional)
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    gutterBottom
                  >
                    To add one, their name, phone and photo are all needed
                    together — a photo alone cannot add a person to the pickup
                    list.
                  </Typography>
                  <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                    <TextField
                      label="Name"
                      variant="outlined"
                      size="small"
                      style={{ flex: 1, minWidth: 160 }}
                      value={fixFields.adult2_name || ''}
                      onChange={(e) => {
                        // Same event-pooling capture as the sections above.
                        const value = e.target.value
                        setFixFields((f) => ({ ...f, adult2_name: value }))
                      }}
                    />
                    <TextField
                      label="Phone"
                      variant="outlined"
                      size="small"
                      style={{ flex: 1, minWidth: 160 }}
                      value={fixFields.adult2_phone || ''}
                      onChange={(e) => {
                        const value = e.target.value
                        setFixFields((f) => ({ ...f, adult2_phone: value }))
                      }}
                    />
                  </Box>
                  {fixPhotoRow(
                    'adult2',
                    { present: false },
                    fixFields.adult2_name || ''
                  )}
                </Box>
              ) : null}
            </>
          ) : null}
          {!!fixError && (
            <Box
              mt={1.5}
              style={{
                fontSize: 13,
                lineHeight: 1.45,
                color: '#8a1f11',
                background: '#fdecea',
                border: '1px solid #f5c6cb',
                borderRadius: 8,
                padding: '10px 12px',
                textTransform: 'none'
              }}
            >
              {fixError}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {fixData?.override ? (
            <Button
              onClick={handleFixReset}
              disabled={fixResetting || fixSaving}
            >
              {fixResetting ? 'Resetting…' : 'Reset to form answers'}
            </Button>
          ) : null}
          <Box flexGrow={1} />
          <Button onClick={closeFix} disabled={fixSaving || fixResetting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFixSave}
            disabled={!fixDirty || fixSaving || fixResetting || !fixData}
          >
            {fixSaving ? 'Saving…' : 'Save & generate card'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sopOpen}
        onClose={() => setSopOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.lcPaper }}
      >
        <DialogTitle>How Parent Onboarding works</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <b>Fill the board with refresh.</b> Press <b>refresh</b> (⟳, next to
            the search box) to pull in two things — children with an active
            School Care <b>enrolment</b>, and parents who have submitted the
            onboarding <b>form</b> (which ticks <b>Onboarding form submitted</b>{' '}
            for you). Refresh whenever you want the latest — for now nothing is
            pulled in on its own.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <b>Pick your centre</b> at the top right, then:
          </Typography>
          <ol style={{ paddingLeft: 18, lineHeight: 1.7 }}>
            <li>
              Send the onboarding form to the parent (send icon on the row) if
              they have not filled it yet. If a parent cannot find the form,
              press <b>Copy enrolment form</b> at the top — it copies your
              centre&apos;s link, ready to paste into WhatsApp.
            </li>
            <li>
              Work the checklist, ticking each step as the team completes it:
              Orientation, Welcome Kit, Teacher–Family 1:1, WhatsApp community
              group, Centre Director first-week check-in.
            </li>
            <li>
              Open the <b>ID card</b> (card icon on the row) to preview it, then{' '}
              <b>Send</b> it to the parent on <b>WhatsApp</b> — this ticks{' '}
              <b>Child ID card shared</b>. You can also <b>Download PNG</b> and
              send it yourself.
            </li>
            <li>
              If the card is blocked (a bad or missing photo), press{' '}
              <b>Fix card details</b> in the card dialog — upload the correct
              photo or fix a name/phone yourself, and the card generates
              immediately. The parent&apos;s form answers are never changed, and{' '}
              <b>reset to form answers</b> undoes every fix.
            </li>
          </ol>
          <Typography variant="body2" gutterBottom>
            <b>Alfred never messages a parent on its own.</b> Nothing goes out
            until someone here presses Send. Reminders and follow-ups are sent
            by the centre team by hand.
          </Typography>
          <Typography variant="caption" color="textSecondary">
            The card is generated automatically from the form (child +
            authorised pickup adults) and goes out as an image. It is only ever
            sent to the parent&apos;s number as registered in Alfred — never to a
            pickup adult&apos;s number from the form. Progress shows how many of
            the steps are done.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSopOpen(false)} color="primary">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ParentOnboarding
